import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Checkbox,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Send as InviteIcon,
  CheckCircle as ActiveIcon,
  Schedule as PendingIcon,
  MoreVert as MoreIcon
} from '@mui/icons-material';

const PersonCard = ({
  person,
  personType,
  location,
  selected,
  onSelect,
  onClick,
  onEdit,
  onDelete,
  onInvite,
  canEdit,
  canDelete,
  canInvite,
  showSelection
}) => {
  const getStatusChip = () => {
    const status = person.inviteStatus || 'none';

    switch (status) {
      case 'pending':
        return (
          <Chip
            icon={<PendingIcon sx={{ fontSize: 14 }} />}
            label="Pending"
            size="small"
            color="warning"
            variant="outlined"
          />
        );
      case 'accepted':
        return (
          <Chip
            icon={<ActiveIcon sx={{ fontSize: 14 }} />}
            label="Active"
            size="small"
            color="success"
          />
        );
      case 'expired':
        return (
          <Chip
            label="Expired"
            size="small"
            color="error"
            variant="outlined"
          />
        );
      default:
        return null;
    }
  };

  const handleCardClick = (e) => {
    // Don't trigger card click if clicking on interactive elements
    if (e.target.closest('button, input, .MuiCheckbox-root')) {
      return;
    }
    onClick?.();
  };

  return (
    <Card
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s',
        '&:hover': onClick ? {
          boxShadow: 3
        } : {},
        ...(selected && {
          borderColor: 'primary.main',
          borderWidth: 2,
          borderStyle: 'solid'
        })
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          {showSelection && (
            <Checkbox
              checked={selected}
              onChange={(e) => onSelect?.(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              size="small"
              sx={{ mt: -0.5, ml: -1 }}
            />
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight="medium" noWrap>
                  {person.friendlyName}
                </Typography>
                {personType && (
                  <Typography variant="body2" color="text.secondary">
                    {personType.friendlyName}
                  </Typography>
                )}
              </Box>
              {getStatusChip()}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" noWrap>
                {location?.friendlyName || 'No location'}
              </Typography>
            </Box>

            {/* Show some custom data fields if available */}
            {personType?.dataModel && person.data && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {personType.dataModel.slice(0, 2).map((field) => {
                  const value = person.data[field.key];
                  if (!value) return null;
                  return (
                    <Chip
                      key={field.key}
                      label={`${field.label}: ${value}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>

      {(canEdit || canDelete || canInvite) && (
        <>
          <Divider />
          <CardActions sx={{ justifyContent: 'flex-end', px: 1, py: 0.5 }}>
            {canInvite && person.inviteStatus !== 'accepted' && (
              <Button
                size="small"
                startIcon={<InviteIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onInvite?.();
                }}
              >
                {person.inviteStatus === 'pending' ? 'Resend' : 'Invite'}
              </Button>
            )}
            {canEdit && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.();
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {canDelete && (
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.();
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </CardActions>
        </>
      )}
    </Card>
  );
};

export default PersonCard;
